import { useState, useMemo } from 'react';

// UsageHeatmap — visualizes feature usage (sheets, protege, workflows, chat,
// events/rooms) across days for a trial or customer. Data source:
// trial_usage_journals.events_json (from the Amplitude Export API pipeline).
//
// Props:
//   events        — compacted event array from events_json (trial_usage_journals)
//   defaultDays   — default timeframe (7, 14, or 30 days)

const FEATURE_FAMILIES = [
  {
    id: 'chat',
    label: 'Chat',
    color: '#3b82f6', // blue
    events: ['chat_question'],
  },
  {
    id: 'protege',
    label: 'Protégé',
    color: '#8b5cf6', // purple
    events: ['protege_message'],
  },
  {
    id: 'sheets',
    label: 'Sheets',
    color: '#10b981', // green
    events: [
      'create_implied_sheet', 'open_implied_sheet', 'upload_implied_sheet',
      'share_implied_sheet', 'import_shared_implied_sheet',
    ],
  },
  {
    id: 'workflows',
    label: 'Workflows',
    color: '#f59e0b', // amber
    // Exactly the usage-rollup taxonomy (jobs/usage-rollup/rollups.py):
    // generate_* presets + custom. click_workflow_template / init_workflow are
    // deliberately excluded — every run fires one of those AND its generate_*
    // event, so counting both doubles each workflow run.
    events: [
      'generate_earnings_summaries', 'generate_debt_screener', 'generate_conference_prep',
      'generate_catalyst_events', 'generate_conference_recap',
      'create_custom_workflow', 'run_custom_workflow', 'import_shared_workflow',
    ],
  },
  {
    id: 'events',
    label: 'Events',
    color: '#ec4899', // pink
    // join_room only — counting leave_room too would double every attendance
    events: ['join_room'],
  },
];

// Dashboards row (IMP-1852): stock-dashboard views only — the explicit
// stock_tab_viewed event plus page views on /stock/<TICKER>. Generic '/',
// /chat, /events page views deliberately do NOT count. Rendered separated
// from the feature families and excluded from features_used/health math.
const DASHBOARD_ROW = {
  id: 'dashboards',
  label: 'Dashboards',
  color: '#64748b', // slate — visually apart from the feature palette
  match: (ev) => ev.type === 'stock_tab_viewed'
    || (ev.type === 'page_view' && /^\/stock\//.test(ev.props?.path ?? '')),
};

// Visits row: everything that isn't a feature or dashboard event — page
// views, sign-ins, other navigation. Presence, not usage: a customer who
// signed in this morning but only browsed must not render as a fully blank
// heatmap ("no usage data" next to a last-login of today reads as a bug).
// Display-only — excluded from isCountedEvent, so the EVENTS stat tile and
// features/health math are unchanged.
const VISITS_ROW = {
  id: 'visits',
  label: 'Visits',
  color: '#94a3b8', // light slate — clearly secondary to the feature palette
};

const ALL_ROWS = [...FEATURE_FAMILIES, DASHBOARD_ROW];

function rowFor(ev) {
  return FEATURE_FAMILIES.find((f) => f.events.includes(ev.type))
    ?? (DASHBOARD_ROW.match(ev) ? DASHBOARD_ROW : null);
}

// "Doing something" — mirrors backend amplitude-journal.js isCountedEvent.
// Deliberately does NOT include the Visits row.
export function isCountedEvent(ev) {
  return rowFor(ev) != null;
}

// One dashboard visit fires page_view /stock/X PLUS stock_tab_viewed (per
// tab, including the default tab on load), so the Dashboards row counted
// each visit 2-3×. Collapse to one event per user + ticker + minute, keeping
// a stock_tab_viewed representative over the page_view so the tooltip reads
// "Viewed WFC dashboard". Non-dashboard events pass through untouched.
function dashboardTicker(ev) {
  const p = ev.props ?? {};
  if (ev.type === 'stock_tab_viewed') {
    const t = p.ticker ?? (p.path?.startsWith('/stock/') ? p.path.slice('/stock/'.length) : null);
    return t != null ? String(t).toUpperCase().split(/[/?#]/)[0] : '';
  }
  if (ev.type === 'page_view' && /^\/stock\//.test(p.path ?? '')) {
    return String(p.path.slice('/stock/'.length)).toUpperCase().split(/[/?#]/)[0];
  }
  return null; // not a dashboard event
}

export function dedupeDashboardEvents(events) {
  const out = [];
  const kept = new Map(); // uid|ticker|minute -> index into out
  for (const ev of events ?? []) {
    const ticker = dashboardTicker(ev);
    if (ticker == null) { out.push(ev); continue; }
    const key = `${ev.uid}|${ticker}|${Math.floor(ev.utcMs / 60_000)}`;
    const at = kept.get(key);
    if (at === undefined) {
      kept.set(key, out.length);
      out.push(ev);
    } else if (ev.type === 'stock_tab_viewed' && out[at].type !== 'stock_tab_viewed') {
      out[at] = ev; // upgrade the page_view representative in place
    }
  }
  return out;
}

export const TIMEFRAMES = [
  { days: 7,   label: '7 days' },
  { days: 14,  label: '14 days' },
  { days: 30,  label: '30 days' },
  { days: 90,  label: '90 days' },
  { days: 180, label: '6 months' },
];

const PT_TZ = 'America/Los_Angeles';

export function ptDate(utcMs) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: PT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date(utcMs)); // 'YYYY-MM-DD'
}

export function ptTime(utcMs) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: PT_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${fmt.format(new Date(utcMs))} PT`;
}

function formatDateHeader(ymd) {
  const d = new Date(`${ymd}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function getEventDetail(ev) {
  const p = ev.props ?? {};
  switch (ev.type) {
    case 'chat_question':
      return p.prompt
        ? `"${String(p.prompt).slice(0, 100)}${String(p.prompt).length > 100 ? '...' : ''}"`
        : 'Chat question';
    case 'protege_message':
      return p.prompt
        ? `"${String(p.prompt).slice(0, 100)}${String(p.prompt).length > 100 ? '...' : ''}"`
        : 'Protégé message';
    case 'create_implied_sheet':
      return `Created sheet${p.title ? ` "${p.title}"` : ''}`;
    case 'open_implied_sheet':
      return `Opened sheet${p.title ? ` "${p.title}"` : p.sheetId ? ` ${p.sheetId}` : ''}`;
    case 'upload_implied_sheet':
      return `Uploaded sheet${p.title ? ` "${p.title}"` : ''}`;
    case 'share_implied_sheet':
      return `Shared sheet${p.title ? ` "${p.title}"` : ''}`;
    case 'import_shared_implied_sheet':
      return `Imported shared sheet${p.title ? ` "${p.title}"` : ''}`;
    case 'generate_debt_screener':
    case 'generate_conference_prep':
    case 'generate_catalyst_events':
    case 'generate_conference_recap': {
      const label = {
        generate_debt_screener: 'Debt screener', generate_conference_prep: 'Conference prep',
        generate_catalyst_events: 'Catalyst events', generate_conference_recap: 'Conference recap',
      }[ev.type];
      return `${label}${p.tickers ? ` for ${Array.isArray(p.tickers) ? p.tickers.join(', ') : p.tickers}` : ''}`;
    }
    case 'create_custom_workflow':
      return `Created custom workflow${p.workflow ? ` "${p.workflow}"` : ''}`;
    case 'run_custom_workflow':
      return `Ran custom workflow${p.workflow ? ` "${p.workflow}"` : ''}`;
    case 'import_shared_workflow':
      return `Imported shared workflow${p.workflow ? ` "${p.workflow}"` : ''}`;
    case 'click_workflow_template':
      return `Template${p.workflow ? `: ${p.workflow}` : ''}`;
    case 'init_workflow':
      return `Started${p.workflow ? `: ${p.workflow}` : ''}`;
    case 'generate_earnings_summaries':
      return `Earnings summaries${p.tickers ? ` for ${Array.isArray(p.tickers) ? p.tickers.join(', ') : p.tickers}` : ''}`;
    case 'stock_tab_viewed': {
      const t = p.ticker ?? (p.path?.startsWith('/stock/') ? p.path.slice('/stock/'.length) : null);
      return `Viewed${t ? ` ${t}` : ''} dashboard`;
    }
    case 'page_view': {
      const t = p.path?.startsWith('/stock/') ? p.path.slice('/stock/'.length) : null;
      return t ? `Viewed ${t} dashboard page` : p.path ? `Page view ${p.path}` : 'Page view';
    }
    case 'join_room':
      return `Joined${p.room ? `: ${p.room}` : ''}`;
    case 'leave_room':
      return `Attended${p.room ? `: ${p.room}` : ''}`;
    default:
      return ev.type;
  }
}

export default function UsageHeatmap({ events = [], defaultDays = 7, timeframe: timeframeProp, onTimeframeChange }) {
  // Controlled when the parent passes `timeframe` (UsageJournalPanel syncs
  // the journal stats row to it); self-managed otherwise.
  const [tfState, setTfState] = useState(defaultDays);
  const timeframe = timeframeProp ?? tfState;
  const setTimeframe = (d) => { if (timeframeProp == null) setTfState(d); onTimeframeChange?.(d); };

  const { dates, heatmapData, maxCount, emptyDates } = useMemo(() => {
    // Full window: today back N days (PT), rendered even where empty — a quiet
    // last few days should show as gaps, not shrink the axis.
    // (Most recent first for display; the Set dedupes a possible DST repeat.)
    const now = Date.now();
    const dates = [];
    const inWindow = new Set();
    for (let i = 0; i < timeframe; i++) {
      const ymd = ptDate(now - i * 86_400_000);
      if (!inWindow.has(ymd)) { inWindow.add(ymd); dates.push(ymd); }
    }

    // Build map: date -> feature -> events[]
    const dataByDate = new Map();
    let max = 0;

    for (const ev of events) {
      const date = ptDate(ev.utcMs);
      if (!inWindow.has(date)) continue;
      // Feature/dashboard events land in their family row; everything else
      // (page views, navigation) lands in Visits — so any day the user was
      // in the product shows life somewhere.
      const family = rowFor(ev) ?? VISITS_ROW;

      if (!dataByDate.has(date)) {
        dataByDate.set(date, new Map());
      }
      const dayData = dataByDate.get(date);

      if (!dayData.has(family.id)) {
        dayData.set(family.id, []);
      }
      dayData.get(family.id).push(ev);
      max = Math.max(max, dayData.get(family.id).length);
    }

    // Days with zero events across all features collapse to slim columns.
    const emptyDates = new Set(dates.filter((d) => !dataByDate.has(d)));

    return { dates, heatmapData: dataByDate, maxCount: max, emptyDates };
  }, [events, timeframe]);

  if (!events.length) {
    return (
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>
        No events data available. Generate a usage journal first.
      </div>
    );
  }

  if (maxCount === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
            Timeframe:
          </span>
          {TIMEFRAMES.map(({ days: d, label }) => (
            <button
              key={d}
              className={`btn btn--ghost btn--sm ${timeframe === d ? 'btn--active' : ''}`}
              style={{ fontSize: 10.5 }}
              onClick={() => setTimeframe(d)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>
          No activity in the last {timeframe} days.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Timeframe selector */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
          Timeframe:
        </span>
        {TIMEFRAMES.map(({ days: d, label }) => (
          <button
            key={d}
            className={`btn btn--ghost btn--sm ${timeframe === d ? 'btn--active' : ''}`}
            style={{ fontSize: 10.5 }}
            onClick={() => setTimeframe(d)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Heatmap */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 6,
          overflow: 'auto',
          background: 'var(--surface-2)',
        }}
      >
        <table
          style={{
            borderCollapse: 'collapse',
            fontSize: 10.5,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  position: 'sticky',
                  left: 0,
                  background: 'var(--surface-2)',
                  padding: '8px 10px',
                  textAlign: 'left',
                  borderRight: '1px solid var(--border)',
                  borderBottom: '1px solid var(--border)',
                  fontWeight: 700,
                  fontSize: 10,
                  color: 'var(--text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '.04em',
                  zIndex: 2,
                }}
              >
                Feature
              </th>
              {dates.map((date) => {
                const isEmpty = emptyDates.has(date);
                return (
                  <th
                    key={date}
                    title={isEmpty ? `${formatDateHeader(date)} — no activity` : undefined}
                    style={{
                      padding: isEmpty ? '8px 2px' : '8px 6px',
                      textAlign: 'center',
                      borderBottom: '1px solid var(--border)',
                      fontWeight: isEmpty ? 500 : 600,
                      fontSize: isEmpty ? 8.5 : 10,
                      color: isEmpty ? 'var(--text-3)' : 'var(--text-2)',
                      minWidth: isEmpty ? 18 : 50,
                    }}
                  >
                    {isEmpty ? date.slice(8) : formatDateHeader(date)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {[...ALL_ROWS, VISITS_ROW].map((family) => (
              <tr key={family.id} style={family.id === 'dashboards' || family.id === 'visits' ? { borderTop: '2px solid var(--border)' } : undefined}>
                <td
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: 'var(--surface-2)',
                    padding: '10px',
                    borderRight: '1px solid var(--border)',
                    fontWeight: 600,
                    fontSize: 11,
                    color: 'var(--text)',
                    zIndex: 1,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background: family.color,
                      }}
                    />
                    {family.label}
                  </div>
                </td>
                {dates.map((date) => {
                  const dayData = heatmapData.get(date);
                  const familyEvents = dayData?.get(family.id) ?? [];
                  const count = familyEvents.length;
                  const intensity = maxCount > 0 ? count / maxCount : 0;

                  return (
                    <HeatmapCell
                      key={date}
                      count={count}
                      intensity={intensity}
                      color={family.color}
                      events={familyEvents}
                      family={family}
                      compact={emptyDates.has(date)}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ fontSize: 10, color: 'var(--text-3)', fontStyle: 'italic' }}>
        Cell intensity = usage volume for that feature on that day. Hover for details.
        Visits = page views / navigation with no feature use (not counted as usage).
      </div>
    </div>
  );
}

function HeatmapCell({ count, intensity, color, events, family, compact = false }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    if (count === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const bgColor = count === 0
    ? 'transparent'
    : `${color}${Math.round(15 + intensity * 85).toString(16).padStart(2, '0')}`;

  return (
    <>
      <td
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          padding: compact ? '8px 2px' : 8,
          textAlign: 'center',
          background: bgColor,
          cursor: count > 0 ? 'pointer' : 'default',
          transition: 'background 0.15s ease',
          position: 'relative',
          fontWeight: count > 0 ? 600 : 400,
          color: count > 0 ? 'var(--text)' : 'var(--text-3)',
          fontSize: compact ? 8.5 : 10.5,
        }}
      >
        {count > 0 ? count : compact ? '·' : '—'}
      </td>

      {showTooltip && count > 0 && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: 'translate(-50%, -100%)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '8px 10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: 320,
            fontSize: 11,
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>
            {family.label} — {count} {count === 1 ? 'event' : 'events'}
          </div>
          <div
            style={{
              maxHeight: 200,
              overflowY: 'auto',
              color: 'var(--text-2)',
            }}
          >
            {events.slice(0, 20).map((ev, i) => (
              <div key={i} style={{ marginTop: i > 0 ? 3 : 0, fontSize: 10.5 }}>
                <span style={{ color: 'var(--text-3)', fontSize: 9.5 }}>
                  {ptTime(ev.utcMs)}
                </span>{' '}
                {getEventDetail(ev)}
              </div>
            ))}
            {events.length > 20 && (
              <div style={{ marginTop: 3, fontSize: 10, color: 'var(--text-3)', fontStyle: 'italic' }}>
                +{events.length - 20} more
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
